import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  Tabs,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  ArrowClockwise,
  ArrowRight,
  Clock,
  Phone,
  EnvelopeSimple,
  CalendarCheck,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { apiClient } from "../../../lib/api";
import { ResponsiveTable } from "../../../components/common/ResponsiveTable";
import type {
  SalesContact,
  SalesDeskData,
  SalesCommand,
} from "../../../types/salesDesk";
import { age, base, duration, load, stamp } from "./shared";
import { SalesSourcesPanel } from "./SalesSourcesPanel";
import { SalesPlaybooksPanel } from "./SalesPlaybooksPanel";
import { SalesSettingsPanel } from "./SalesSettingsPanel";

export default function SalesDeskPage() {
  const cache = useQueryClient();
  const [tab, setTab] = useState(0),
    [page, setPage] = useState(1),
    [search, setSearch] = useState(""),
    [assignee, setAssignee] = useState(""),
    [reason, setReason] = useState("");
  const [selected, setSelected] = useState<SalesContact | null>(null),
    [notice, setNotice] = useState("");
  const retry = useRef<{ payload: string; id: string } | null>(null);
  const params = new URLSearchParams({
    page: String(page),
    search,
    assignee,
    reason,
  });
  const query = useQuery({
    queryKey: ["sales-desk", page, search, assignee, reason],
    queryFn: () => load<SalesDeskData>(base + "?" + params),
    refetchInterval: 30000,
  });
  const mutation = useMutation({
    mutationFn: async ({
      kind,
      data,
    }: {
      kind: string;
      data: Record<string, unknown>;
    }) => {
      const payload = JSON.stringify({ kind, data });
      if (retry.current?.payload !== payload)
        retry.current = { payload, id: crypto.randomUUID() };
      const result = await apiClient.put(base, {
        kind,
        data,
        request_id: retry.current.id,
      });
      retry.current = null;
      return result.data;
    },
    onSuccess: async () => {
      setNotice("Saved.");
      await cache.invalidateQueries({ queryKey: ["sales-desk"] });
    },
    onError: () => setNotice(""),
  });
  const command: SalesCommand = async (kind, data) =>
    mutation.mutateAsync({ kind, data });
  const data = query.data;
  return (
    <Stack spacing={2} sx={{ minWidth: 0, pb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Typography level="h2">Sales desk</Typography>
          <Typography level="body-md">
            Turn today’s customer signals into the next conversation.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Button component={Link} to="/reports/sales" variant="outlined">
            Sales reports
          </Button>
          <Button
            variant="plain"
            startDecorator={<ArrowClockwise />}
            onClick={() => {
              void cache.invalidateQueries({ queryKey: ["sales-desk"] });
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>
      {query.isPending && <CircularProgress aria-label="Loading sales desk" />}
      {query.isError && <Alert color="danger">{query.error.message}</Alert>}
      {mutation.isError && (
        <Alert color="danger" role="alert">
          {mutation.error.message}
        </Alert>
      )}
      {notice && <Alert color="success">{notice}</Alert>}
      {data && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2,minmax(0,1fr))",
                lg: "repeat(4,minmax(0,1fr))",
              },
              gap: 1.5,
            }}
          >
            {[
              {
                label: "Needs attention",
                value: data.summary.total,
                detail: `${data.summary.unassigned} unassigned`,
              },
              {
                label: "Overdue",
                value: data.summary.overdue,
                detail: `${data.summary.escalated} beyond escalation target`,
              },
              {
                label: "Median first response",
                value: duration(data.summary.median_first_response_minutes),
                detail: `90th percentile: ${duration(data.summary.p90_first_response_minutes)}`,
              },
              {
                label: "Awaiting first response",
                value: data.summary.awaiting_first_response,
                detail: `New leads in the last 30 days`,
              },
            ].map((c) => (
              <Card key={c.label} variant="outlined">
                <Typography level="body-sm">{c.label}</Typography>
                <Typography level="h3">{c.value}</Typography>
                <Typography level="body-xs">{c.detail}</Typography>
              </Card>
            ))}
          </Box>
          <Typography level="body-xs">
            Response metrics cover the last 30 days of visible new contacts;{" "}
            {data.summary.response_sample} have a confirmed first response.
            Updated {stamp(data.as_of)}. Times are elapsed clock time.
          </Typography>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(Number(v))}
            sx={{ bgcolor: "transparent", minWidth: 0 }}
          >
            <TabList sx={{ flexWrap: "wrap" }}>
              <Tab value={0}>Daily worklist</Tab>
              {data.permissions.playbooks && (
                <Tab value={1}>Follow-up playbooks</Tab>
              )}
              {data.permissions.sources && (
                <Tab value={2}>Source profitability</Tab>
              )}
              {data.permissions.configure && <Tab value={3}>Team settings</Tab>}
            </TabList>
          </Tabs>
          {tab === 0 && (
            <>
              <CollapsibleFilters><Stack direction={{ xs: "column", md: "row" }} gap={1.5}>
                <Input
                  aria-label="Search contacts"
                  placeholder="Search contacts"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  sx={{ flex: 1 }}
                />
                <Select
                  aria-label="Assigned rep"
                  value={assignee}
                  onChange={(_, v) => {
                    setAssignee(v || "");
                    setPage(1);
                  }}
                  sx={{ minWidth: 170 }}
                >
                  <Option value="">All visible reps</Option>
                  <Option value="unassigned">Unassigned</Option>
                  {data.members.map((m) => (
                    <Option key={m.id} value={String(m.id)}>
                      {m.name}
                    </Option>
                  ))}
                </Select>
                <Select
                  aria-label="Attention reason"
                  value={reason}
                  onChange={(_, v) => {
                    setReason(v || "");
                    setPage(1);
                  }}
                  sx={{ minWidth: 180 }}
                >
                  <Option value="">All reasons</Option>
                  <Option value="initial_outreach">Initial outreach</Option>
                  <Option value="reply_waiting">Customer response</Option>
                  <Option value="overdue_followup">Overdue follow-up</Option>
                  <Option value="proposal_next_step">Viewed proposal</Option>
                </Select>
              </Stack></CollapsibleFilters>
              <Card variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
                <ResponsiveTable<SalesContact>
                  rows={data.rows}
                  getRowKey={(row) => row.id}
                  pagination={{
                    page,
                    pageSize: 25,
                    totalCount: data.count,
                    onPageChange: setPage,
                    itemNoun: "contacts",
                  }}
                  emptyState="No contacts need attention with these filters."
                  columns={[
                    {
                      key: "contact",
                      header: "Contact",
                      mobileVariant: "header",
                      render: (row) => (
                        <Box>
                          <Typography
                            component={Link}
                            to={"/crm/contacts/" + row.id}
                            level="title-md"
                          >
                            {row.name}
                          </Typography>
                          <Typography level="body-xs">
                            {row.owner} · {row.source}
                          </Typography>
                        </Box>
                      ),
                    },
                    {
                      key: "reason",
                      header: "Why now",
                      mobileVariant: "body",
                      render: (row) => (
                        <Stack gap={0.5} alignItems="flex-start">
                          {row.reasons.map((item) => (
                            <Chip
                              key={item.kind}
                              size="sm"
                              color={
                                row.escalate
                                  ? "danger"
                                  : row.overdue
                                    ? "warning"
                                    : "neutral"
                              }
                              variant="soft"
                              sx={{ whiteSpace: "normal" }}
                            >
                              {item.label}
                            </Chip>
                          ))}
                        </Stack>
                      ),
                    },
                    {
                      key: "outreach",
                      header: "Last outreach",
                      render: (row) => (
                        <Box>
                          <Typography level="body-sm">
                            {age(row.last_outreach)}
                          </Typography>
                          <Typography level="body-xs">
                            {row.reached
                              ? "Delivery or conversation confirmed"
                              : row.attempts
                                ? row.attempts +
                                  " attempts · no confirmed connection"
                                : "No delivery or conversation confirmed"}
                          </Typography>
                        </Box>
                      ),
                    },
                    {
                      key: "due",
                      header: "Next step",
                      render: (row) => (
                        <Box>
                          <Typography level="body-sm">
                            {stamp(row.next_followup || row.due_at)}
                          </Typography>
                          {row.escalate && (
                            <Typography color="danger" level="body-xs">
                              Manager attention needed
                            </Typography>
                          )}
                        </Box>
                      ),
                    },
                  ]}
                  rowActions={(row) => (
                    <Button
                      size="sm"
                      variant="soft"
                      endDecorator={<ArrowRight />}
                      onClick={() => setSelected(row)}
                    >
                      Review
                    </Button>
                  )}
                />
              </Card>
              <Alert variant="soft" color="neutral" startDecorator={<Clock />}>
                Target: first response within{" "}
                {duration(data.policy.response_minutes)}; escalation after{" "}
                {duration(data.policy.escalation_minutes)}. A note or an
                unanswered call does not count as a successful connection.
              </Alert>
            </>
          )}
          {tab === 1 && (
            <SalesPlaybooksPanel
              data={data}
              command={command}
              busy={mutation.isPending}
            />
          )}
          {tab === 2 && (
            <SalesSourcesPanel
              command={command}
              busy={mutation.isPending}
              canEdit={data.permissions.manage_sources}
            />
          )}
          {tab === 3 && (
            <SalesSettingsPanel
              data={data}
              command={command}
              busy={mutation.isPending}
            />
          )}
        </>
      )}
      <Modal open={!!selected} onClose={() => setSelected(null)}>
        {selected && data ? (
          <ModalDialog
            sx={{
              width: 760,
              maxWidth: "calc(100vw - 24px)",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <ModalClose />
            <ContactReview
              key={selected.version}
              contact={selected}
              data={data}
              command={command}
              busy={mutation.isPending}
              done={() => setSelected(null)}
            />
          </ModalDialog>
        ) : (
          <Box />
        )}
      </Modal>
    </Stack>
  );
}

function ContactReview({
  contact,
  data,
  command,
  busy,
  done,
}: {
  contact: SalesContact;
  data: SalesDeskData;
  command: SalesCommand;
  busy: boolean;
  done: () => void;
}) {
  const [owner, setOwner] = useState(
      contact.owner_id ? String(contact.owner_id) : "",
    ),
    [next, setNext] = useState(
      contact.next_followup
        ? new Date(
            new Date(contact.next_followup).getTime() -
              new Date(contact.next_followup).getTimezoneOffset() * 60000,
          )
            .toISOString()
            .slice(0, 16)
        : "",
    ),
    [note, setNote] = useState(""),
    [error, setError] = useState("");
  const save = async () => {
    try {
      await command("next_step", {
        contact: contact.id,
        version: contact.version,
        assigned_to: owner ? Number(owner) : null,
        next_followup: next ? new Date(next).toISOString() : null,
        note,
      });
      done();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Stack spacing={2}>
      <Box sx={{ pr: 4 }}>
        <Typography level="h3">{contact.name}</Typography>
        <Typography>
          {contact.owner} · {contact.source}
        </Typography>
      </Box>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip
          color={contact.preferences.call ? "success" : "danger"}
          startDecorator={<Phone />}
        >
          Calls {contact.preferences.call ? "allowed" : "opted out"}
        </Chip>
        <Chip
          color={contact.preferences.email ? "success" : "danger"}
          startDecorator={<EnvelopeSimple />}
        >
          Email {contact.preferences.email ? "allowed" : "opted out"}
        </Chip>
        <Button
          component={Link}
          to={`/crm/contacts/${contact.id}`}
          size="sm"
          variant="outlined"
        >
          Open contact to call or email
        </Button>
        <Button
          component={Link}
          to="/scheduling/meetings"
          size="sm"
          variant="outlined"
          startDecorator={<CalendarCheck />}
        >
          Meetings
        </Button>
      </Stack>
      {error && <Alert color="danger">{error}</Alert>}
      {data.permissions.edit && (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Assigned rep</FormLabel>
              <Select value={owner} onChange={(_, v) => setOwner(v || "")}>
                <Option value="">Unassigned</Option>
                {data.members.map((m) => (
                  <Option value={String(m.id)} key={m.id}>
                    {m.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Next follow-up</FormLabel>
              <Input
                type="datetime-local"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
            </FormControl>
          </Stack>
          <FormControl>
            <FormLabel>Internal next-step note</FormLabel>
            <Textarea
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What should happen next? This is logged as a note."
            />
          </FormControl>
          <Button onClick={() => void save()} loading={busy}>
            Save next step
          </Button>
        </>
      )}
      <Divider />
      <Typography level="title-lg">Activity evidence</Typography>
      <Typography level="body-sm">
        Message delivery state is separate from provider acceptance. Manually
        logged activity stays labeled.
      </Typography>
      {contact.events.length === 0 && (
        <Typography level="body-sm">
          No linked communication evidence yet.
        </Typography>
      )}
      {contact.events.map((event) => (
        <Box
          key={event.key}
          sx={{
            borderLeft: "3px solid",
            borderColor: event.verified ? "primary.300" : "neutral.300",
            pl: 1.5,
            py: 0.5,
          }}
        >
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Typography level="title-sm">
              {event.kind.replaceAll("_", " ")}
            </Typography>
            {!event.verified && <Chip size="sm">Manual</Chip>}
            <Typography level="body-xs">{stamp(event.at)}</Typography>
          </Stack>
          <Typography level="body-sm">{event.label}</Typography>
          <Typography level="body-xs">Evidence: {event.source}</Typography>
        </Box>
      ))}
      <Typography level="title-md">Next-step history</Typography>
      {data.history
        .filter((h) => Number(h.data.contact) === contact.id)
        .map((h) => (
          <Typography key={h.id} level="body-sm">
            {h.actor} · {stamp(h.created_at)} ·{" "}
            {String(h.data.note || h.kind.replaceAll("_", " "))}
          </Typography>
        ))}
    </Stack>
  );
}
